import { priceEngine } from '@/services/price-engine'
import type { QuoteLineItem, QuoteTotals } from '../types'

/**
 * Quote pricing adapter — delegates all math to PriceEngine (Sprint 0.5).
 * Keeps quote module API stable while isolating calculations.
 */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export function recalcLine(
  item: Omit<QuoteLineItem, 'salePrice' | 'subtotal'> & {
    salePrice?: number
    subtotal?: number
  },
  changed: 'components' | 'salePrice' | 'purchase' | 'profit' = 'components',
): QuoteLineItem {
  const trigger =
    changed === 'salePrice' ? 'sale' : changed === 'purchase' ? 'purchase' : changed === 'profit' ? 'profit' : 'components'

  const result = priceEngine.calculateLine(
    {
      id: item.id,
      quantity: item.quantity,
      purchasePrice: item.purchasePrice,
      operational: item.operational,
      taxes: item.taxes,
      profit: item.profit,
      salePrice: item.salePrice,
    },
    trigger,
  )

  return {
    id: item.id,
    productId: item.productId,
    description: item.description,
    quantity: result.quantity,
    purchasePrice: result.purchasePrice,
    operational: result.operational,
    taxes: result.taxes,
    profit: result.profit,
    salePrice: result.salePrice,
    subtotal: result.subtotal,
    sortOrder: item.sortOrder,
  }
}

export function calcTotals(
  items: QuoteLineItem[],
  discountPercent: number,
  discountValue: number,
  freight: number,
): QuoteTotals {
  const lines = items.map((item) =>
    priceEngine.calculateLine(
      {
        id: item.id,
        quantity: item.quantity,
        purchasePrice: item.purchasePrice,
        operational: item.operational,
        taxes: item.taxes,
        profit: item.profit,
        salePrice: item.salePrice,
      },
      'components',
    ),
  )
  const summary = priceEngine.summarize(lines, discountPercent, discountValue, freight)

  return {
    itemsCount: items.reduce((sum, i) => sum + i.quantity, 0),
    purchaseTotal: summary.purchaseTotal,
    subtotal: summary.saleTotal,
    discount: summary.discount,
    freight: summary.freight,
    total: summary.finalPrice,
    profitTotal: summary.profitNet,
    margin: summary.marginPercent,
  }
}

export function createEmptyLine(sortOrder = 0): QuoteLineItem {
  return recalcLine({
    id: crypto.randomUUID(),
    productId: null,
    description: '',
    quantity: 1,
    purchasePrice: 0,
    operational: 0,
    taxes: 0,
    profit: 0,
    sortOrder,
  })
}

export function createLineFromProduct(input: {
  productId: string
  description: string
  purchasePrice: number
  salePrice?: number
  sortOrder: number
  category?: string | null
}): QuoteLineItem {
  const id = crypto.randomUUID()
  const purchase = Math.max(0, input.purchasePrice)
  const priced = priceEngine.calculateLine(
    {
      id,
      quantity: 1,
      purchasePrice: purchase,
      salePrice: input.salePrice,
      category: input.category,
    },
    input.salePrice != null ? 'sale' : 'purchase',
  )

  return {
    id,
    productId: input.productId,
    description: input.description,
    quantity: priced.quantity,
    purchasePrice: priced.purchasePrice,
    operational: priced.operational,
    taxes: priced.taxes,
    profit: priced.profit,
    salePrice: priced.salePrice,
    subtotal: priced.subtotal,
    sortOrder: input.sortOrder,
  }
}

export function lineMarginPercent(item: QuoteLineItem): number {
  if (item.salePrice <= 0) return 0
  return round2((item.profit / item.salePrice) * 100)
}
